'use client';

/**
 * CertificationsByTrack — Expandable certificate list grouped by track.
 *
 * SPEC-080 UX improvement: instead of showing all certificates in a flat
 * list (which gets very long when the user has 50+ certificates), group
 * them by track (Capacitaciones Profileia / Academia de Emprendimiento)
 * in expandable sections. Each section is a clickable header showing the
 * track name + cert count. Clicking expands to show the certificates.
 *
 * This is a client component because it uses useState for the expand/collapse
 * interaction. The parent page (/p/[username]) is a server component (SSR)
 * that fetches the data and passes it as props.
 *
 * CM-29 (Accessibility): each section has role="button", aria-expanded,
 * aria-label, and supports keyboard navigation (Enter/Space to toggle).
 */

import { useState } from 'react';
import type { PublicCertification } from './types';

const TRACK_LABELS: Record<string, string> = {
  profileia: 'Capacitaciones Profileia',
  entrepreneurship: 'Academia de Emprendimiento',
};

const TRACK_COLORS: Record<string, { bg: string; text: string; badge: string }> = {
  profileia: {
    bg: 'bg-[#2563EB]/10',
    text: 'text-[#60A5FA]',
    badge: 'bg-[#2563EB]/20 text-[#60A5FA]',
  },
  entrepreneurship: {
    bg: 'bg-[#8B5CF6]/10',
    text: 'text-[#A78BFA]',
    badge: 'bg-[#8B5CF6]/20 text-[#A78BFA]',
  },
};

export function CertificationsByTrack({
  certifications,
}: {
  certifications: PublicCertification[];
}) {
  const [expandedTrack, setExpandedTrack] = useState<string | null>(
    // Default: expand the first track that has certificates
    certifications[0]?.track ?? null,
  );

  // Group by track
  const byTrack = certifications.reduce<Record<string, PublicCertification[]>>(
    (acc, cert) => {
      const track = cert.track || 'other';
      if (!acc[track]) acc[track] = [];
      acc[track].push(cert);
      return acc;
    },
    {},
  );

  const tracks = Object.keys(byTrack).sort((a, b) => {
    // Profileia first, then entrepreneurship, then others
    if (a === 'profileia') return -1;
    if (b === 'profileia') return 1;
    if (a === 'entrepreneurship') return -1;
    if (b === 'entrepreneurship') return 1;
    return a.localeCompare(b);
  });

  return (
    <div className="space-y-3">
      {tracks.map((track) => {
        const trackCerts = byTrack[track];
        const trackLabel = TRACK_LABELS[track] || track;
        const colors = TRACK_COLORS[track] || TRACK_COLORS.entrepreneurship;
        const isExpanded = expandedTrack === track;

        return (
          <div key={track} className="rounded-lg border border-slate-700 overflow-hidden">
            {/* Header — clickable */}
            <button
              type="button"
              onClick={() => setExpandedTrack(isExpanded ? null : track)}
              className="w-full flex items-center gap-3 p-3 hover:bg-slate-800 transition-colors text-left"
              aria-expanded={isExpanded}
              aria-label={`${trackLabel}: ${trackCerts.length} certificados. ${isExpanded ? 'Contraer' : 'Expandir'}`}
            >
              <div className={`rounded-lg ${colors.bg} p-1.5 flex-shrink-0`}>
                <span className="text-base" aria-hidden="true">
                  {track === 'profileia' ? '📘' : '🚀'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white">{trackLabel}</p>
                <p className="text-xs text-slate-400">
                  {trackCerts.length} {trackCerts.length === 1 ? 'certificado' : 'certificados'}
                </p>
              </div>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${colors.badge}`}
              >
                {trackCerts.length}
              </span>
              <svg
                className={`w-4 h-4 text-slate-400 transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden="true"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>

            {/* Expanded certificates */}
            {isExpanded && (
              <ul className="divide-y divide-slate-700/50">
                {trackCerts.map((cert) => {
                  const issuedDate = new Date(cert.issuedAt).toLocaleDateString('es-CO', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  });
                  return (
                    <li key={cert.certificateNumber}>
                      <a
                        href={cert.verificationUrl}
                        className="block p-3 hover:bg-slate-800/50 transition-colors"
                        aria-label={`Certificado: ${cert.courseTitle} — emitido ${issuedDate} — verificar`}
                      >
                        <div className="flex items-start justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-white font-medium truncate">
                              {cert.courseTitle}
                            </p>
                          </div>
                          <span
                            className={`text-[10px] px-1.5 py-0.5 rounded shrink-0 ${colors.badge}`}
                          >
                            {track === 'profileia' ? 'Profileia' : 'Empr.'}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1.5 text-xs">
                          <span className="text-slate-400">
                            Emitido: <time dateTime={cert.issuedAt}>{issuedDate}</time>
                          </span>
                          <span className="text-[#06B6D4] inline-flex items-center gap-1">
                            Verificar ↗
                          </span>
                        </div>
                      </a>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}
